import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listarRecursos,
  subirRecurso,
  getLibraryCategories,
  getLibrarySubcategories,
  createLibrarySubcategory,
  listarResourceTags,
} from "../api";
import "./ResourcePage.css";
import NavBar from "./NavBar";

const emptyFilters = {
  search: "",
  age: "",
  area: "",
  specialty: "",
  resource_type: "",
  difficulty: "",
  tag: "",
};

const emptyForm = {
  title: "",
  description: "",
  age_min: "",
  age_max: "",
  area: "",
  area_folder_id: "",
  subarea: "",
  subarea_folder_id: "",
  objective: "",
  specialties: [],
  tags: [],
  resource_type: "",
  difficulty: "",
  notes: "",
};

const SPECIALTY_OPTIONS = [
  { value: "psicologia", label: "Psicología" },
  { value: "logopedia", label: "Logopedia" },
  { value: "psicopedagogia", label: "Psicopedagogía" },
  { value: "nutricion", label: "Nutrición" },
  { value: "evaluacion", label: "Evaluación" },
];

const specialtyLabel = (value) =>
  SPECIALTY_OPTIONS.find((option) => option.value === value)?.label || value;

const truncateCardText = (text, maxLength = 120) => {
  if (!text) return "Sin descripción.";

  const cleanText = String(text).trim();

  if (cleanText.length <= maxLength) {
    return cleanText;
  }

  return `${cleanText.slice(0, maxLength).trim()}...`;
};

export default function ResourcePage() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("buscar");
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [filterCategories, setFilterCategories] = useState([]);
  const [formCategories, setFormCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [newSubcategoryName, setNewSubcategoryName] = useState("");

  const [availableTags, setAvailableTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [tagToAdd, setTagToAdd] = useState("");
  const [newTagName, setNewTagName] = useState("");

  const [filters, setFilters] = useState(emptyFilters);
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState([]);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const loadResources = async () => {
    setLoading(true);
    setMessage("");

    try {
      const items = await listarRecursos(filters);
      setResources(items);
    } catch (error) {
      setMessage(error.message || "No se pudieron cargar los recursos.");
    } finally {
      setLoading(false);
    }
  };

  const loadFormCategories = async (specialties = form.specialties) => {
    if (!Array.isArray(specialties) || specialties.length === 0) {
      setFormCategories([]);
      return;
    }

    setLoadingCategories(true);

    try {
      const items = await getLibraryCategories(specialties);
      setFormCategories(Array.isArray(items) ? items : []);
    } catch (error) {
      setMessage(error.message || "No se pudieron cargar las áreas.");
      setFormCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadFilterCategories = async (specialty = filters.specialty) => {
    if (!specialty) {
      setFilterCategories([]);
      return;
    }

    try {
      const items = await getLibraryCategories([specialty]);
      setFilterCategories(Array.isArray(items) ? items : []);
    } catch (error) {
      console.error(error);
      setFilterCategories([]);
    }
  };

  const loadTags = async () => {
    setLoadingTags(true);

    try {
      const items = await listarResourceTags();
      setAvailableTags(Array.isArray(items) ? items : []);
    } catch (error) {
      console.error(error);
      setMessage(error.message || "No se pudieron cargar las etiquetas.");
    } finally {
      setLoadingTags(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadResources();
    }, 400);

    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    if (activeTab === "subir") {
      loadFormCategories(form.specialties);
    }
  }, [activeTab, form.specialties]);

  useEffect(() => {
    if (activeTab === "buscar") {
      loadFilterCategories(filters.specialty);
    }
  }, [activeTab, filters.specialty]);

  useEffect(() => {
    loadTags();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "specialty" ? { area: "" } : {}),
      ...(name === "resource_type" && value !== "juego"
        ? { difficulty: "" }
        : {}),
    }));

    if (name === "specialty") {
      setFilterCategories([]);
    }
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    setFilterCategories([]);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      // La dificultad solo tiene sentido para juegos.
      if (name === "resource_type" && value !== "juego") {
        next.difficulty = "";
      }

      return next;
    });
  };

  const toggleSpecialty = (specialty) => {
    // Si había un mensaje de validación anterior, lo quitamos
    // en cuanto el usuario modifica la selección.
    setMessage("");

    setForm((prev) => {
      const selected = prev.specialties.includes(specialty);

      const nextSpecialties = selected
        ? prev.specialties.filter((item) => item !== specialty)
        : [...prev.specialties, specialty];

      return {
        ...prev,
        specialties: nextSpecialties,

        // Al cambiar las especialidades, las áreas disponibles también cambian.
        // Limpiamos la ubicación elegida para evitar guardar el recurso
        // accidentalmente en un área que ya no corresponde a la selección.
        area: "",
        area_folder_id: "",
        subarea: "",
        subarea_folder_id: "",
      };
    });

    setSubcategories([]);
    setNewSubcategoryName("");
  };

  const addExistingTag = (tagName) => {
    if (!tagName) return;

    setForm((prev) => {
      if (prev.tags.includes(tagName)) return prev;

      return {
        ...prev,
        tags: [...prev.tags, tagName],
      };
    });

    setTagToAdd("");
  };

  const removeTag = (tagName) => {
    setForm((prev) => ({
      ...prev,
      tags: prev.tags.filter((item) => item !== tagName),
    }));
  };

  const handleCreateTag = () => {
    const tagName = newTagName.trim();

    if (!tagName) return;

    const existing = availableTags.find(
      (tag) => String(tag.name).toLowerCase() === tagName.toLowerCase()
    );

    const finalName = existing?.name || tagName;

    setAvailableTags((prev) => {
      const alreadyExists = prev.some(
        (tag) => String(tag.name).toLowerCase() === finalName.toLowerCase()
      );

      if (alreadyExists) return prev;

      return [
        ...prev,
        {
          id: `new-${Date.now()}`,
          name: finalName,
        },
      ].sort((a, b) =>
        String(a.name).localeCompare(String(b.name), "es", {
          sensitivity: "base",
        })
      );
    });

    setForm((prev) => ({
      ...prev,
      tags: prev.tags.includes(finalName)
        ? prev.tags
        : [...prev.tags, finalName],
    }));

    setNewTagName("");
  };

  const handleCategoryChange = async (e) => {
    const folderId = e.target.value;
    const selected = formCategories.find(
      (category) => String(category.id) === String(folderId)
    );

    setForm((prev) => ({
      ...prev,
      area: selected?.name || "",
      area_folder_id: folderId,
      subarea: "",
      subarea_folder_id: "",
    }));

    setSubcategories([]);
    setNewSubcategoryName("");

    if (!folderId) return;

    setLoadingSubcategories(true);

    try {
      const items = await getLibrarySubcategories(folderId);
      setSubcategories(Array.isArray(items) ? items : []);
    } catch (error) {
      setMessage(error.message || "No se pudieron cargar las subáreas.");
    } finally {
      setLoadingSubcategories(false);
    }
  };

  const handleSubcategoryChange = (e) => {
    const folderId = e.target.value;
    const selected = subcategories.find(
      (subcategory) => String(subcategory.id) === String(folderId)
    );

    setForm((prev) => ({
      ...prev,
      subarea: selected?.name || "",
      subarea_folder_id: folderId,
    }));
  };

  const handleCreateSubcategory = async () => {
    setMessage("");

    if (!form.area_folder_id) {
      setMessage("Primero selecciona un área.");
      return;
    }

    if (!newSubcategoryName.trim()) {
      setMessage("Escribe el nombre de la nueva subárea.");
      return;
    }

    try {
      const folder = await createLibrarySubcategory(
        form.area_folder_id,
        newSubcategoryName.trim()
      );

      setSubcategories((prev) => [...prev, folder]);

      setForm((prev) => ({
        ...prev,
        subarea: folder.name,
        subarea_folder_id: folder.id,
      }));

      setNewSubcategoryName("");
      setMessage("Subárea creada correctamente.");
    } catch (error) {
      setMessage(error.message || "No se pudo crear la subárea.");
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setFiles([]);
    setFormCategories([]);
    setSubcategories([]);
    setNewSubcategoryName("");
    setTagToAdd("");
    setNewTagName("");
  };

  const removeFile = (indexToRemove) => {
    setFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleUploadResource = async (e) => {
    e.preventDefault();
    setMessage("");

    if (form.specialties.length === 0) {
      setMessage("Debes seleccionar al menos una especialidad.");
      return;
    }

    if (!form.area_folder_id) {
      setMessage("Debes seleccionar un área.");
      return;
    }

    if (!files.length) {
      setMessage("Debes seleccionar al menos un archivo.");
      return;
    }

    try {
      const formData = new FormData();

      Object.entries(form).forEach(([key, value]) => {
        if (key === "specialties" || key === "tags") {
          return;
        }

        formData.append(key, value);
      });

      form.specialties.forEach((specialty) => {
        formData.append("specialties[]", specialty);
      });

      form.tags.forEach((tag) => {
        formData.append("tags[]", tag);
      });

      files.forEach((selectedFile) => {
        formData.append("files[]", selectedFile);
      });

      await subirRecurso(formData);

      setMessage(
        files.length === 1
          ? "Recurso subido correctamente."
          : `${files.length} recursos subidos correctamente.`
      );

      resetForm();
      setActiveTab("buscar");
      await loadResources();
    } catch (error) {
      setMessage(error.message || "No se pudo subir el recurso.");
    }
  };

  const toggleDescription = (resourceId) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [resourceId]: !prev[resourceId],
    }));
  };

  const fileUrl = (path) => {
    if (!path) return "#";
    if (path.startsWith("http")) return path;
    return `https://te-ayudo.es${path}`;
  };

  const openResource = (resource) => {
    if (resource.content_type === "interactive_game") {
      navigate(`/games/${resource.game_key}`);
      return;
    }

    window.open(
      resource.drive_url || fileUrl(resource.file_path),
      "_blank"
    );
  };

  const availableTagsToAdd = availableTags.filter(
    (tag) => !form.tags.includes(tag.name)
  );

  return (
    <>
      <NavBar />

      <div className="resources-page">
        <div className="resources-header">
          <div>
            <h1>Recursos</h1>
            <p>
              Biblioteca clínica interna para buscar, subir y organizar
              materiales.
            </p>
          </div>

          <div className="resources-tabs">
            <button
              type="button"
              className={activeTab === "buscar" ? "active" : ""}
              onClick={() => setActiveTab("buscar")}
            >
              Buscar recursos
            </button>

            <button
              type="button"
              className={activeTab === "subir" ? "active" : ""}
              onClick={() => setActiveTab("subir")}
            >
              Subir recurso
            </button>
          </div>
        </div>

        {message && <div className="resources-message">{message}</div>}

        {activeTab === "buscar" && (
          <>
            <section className="resources-filters">
              <input
                name="search"
                placeholder="Buscar por nombre, descripción u objetivo..."
                value={filters.search}
                onChange={handleFilterChange}
              />

              <select
                name="specialty"
                value={filters.specialty}
                onChange={handleFilterChange}
              >
                <option value="">Especialidad</option>
                {SPECIALTY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <select
                name="area"
                value={filters.area}
                onChange={handleFilterChange}
                disabled={!filters.specialty}
              >
                <option value="">
                  {!filters.specialty ? "Área" : "Todas las áreas"}
                </option>

                {filterCategories.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>

              <input
                name="age"
                type="number"
                placeholder="Edad"
                value={filters.age}
                onChange={handleFilterChange}
              />

              <select
                name="resource_type"
                value={filters.resource_type}
                onChange={handleFilterChange}
              >
                <option value="">Tipo</option>
                <option value="ficha">Ficha</option>
                <option value="juego">Juego</option>
                <option value="cuento">Cuento</option>
                <option value="pauta_padres">Pauta para padres</option>
                    <option value="evaluacion">Evaluación</option>
              </select>

              <select
                name="tag"
                value={filters.tag}
                onChange={handleFilterChange}
              >
                <option value="">
                  {loadingTags ? "Cargando etiquetas..." : "Etiqueta"}
                </option>

                {availableTags.map((tag) => (
                  <option key={tag.id || tag.name} value={tag.name}>
                    {tag.name}
                  </option>
                ))}
              </select>

              {filters.resource_type === "juego" && (
                <select
                  name="difficulty"
                  value={filters.difficulty}
                  onChange={handleFilterChange}
                >
                  <option value="">Dificultad</option>
                  <option value="facil">Fácil</option>
                  <option value="medio">Medio</option>
                  <option value="dificil">Difícil</option>
                </select>
              )}

              <button type="button" onClick={loadResources}>
                Filtrar
              </button>

              <button
                type="button"
                className="clear-filters-btn"
                onClick={clearFilters}
              >
                Limpiar
              </button>
            </section>

            <section className="resources-list">
              {loading ? (
                <p>Cargando recursos...</p>
              ) : resources.length === 0 ? (
                <p>No hay recursos con esos filtros.</p>
              ) : (
                resources.map((resource) => (
                  <article key={resource.id} className="resource-card">
                    <div className="resource-card-header">
                      <h3>{resource.title}</h3>
                      {resource.resource_type === "juego" &&
                        resource.difficulty && (
                          <span>{resource.difficulty}</span>
                        )}
                    </div>

                    <div className="resource-description">
                      <p>
                        {expandedDescriptions[resource.id]
                          ? resource.description || "Sin descripción."
                          : truncateCardText(resource.description)}
                      </p>

                      {resource.description &&
                        String(resource.description).trim().length > 120 && (
                          <button
                            type="button"
                            className="resource-read-more"
                            onClick={() => toggleDescription(resource.id)}
                            aria-expanded={Boolean(
                              expandedDescriptions[resource.id]
                            )}
                          >
                            <span>
                              {expandedDescriptions[resource.id]
                                ? "Leer menos"
                                : "Leer más"}
                            </span>
                            <i
                              className={`fas fa-chevron-${
                                expandedDescriptions[resource.id]
                                  ? "up"
                                  : "down"
                              }`}
                            ></i>
                          </button>
                        )}
                    </div>

                    <div className="resource-tags">
                      {resource.area && <small>{resource.area}</small>}

                      {resource.subarea && (
                        <small>{resource.subarea}</small>
                      )}

                      {Array.isArray(resource.specialties) &&
                        resource.specialties.map((specialty) => (
                          <small key={specialty}>
                            {specialtyLabel(specialty)}
                          </small>
                        ))}

                      {resource.resource_type && (
                        <small>{resource.resource_type}</small>
                      )}

                      {Array.isArray(resource.tags) &&
                        resource.tags.map((tag) => {
                          const tagName = tag?.name || tag;

                          return (
                            <small key={tag?.id || tagName}>
                              {tagName}
                            </small>
                          );
                        })}

                      {resource.content_type === "interactive_game" && (
                        <small>Juego interactivo</small>
                      )}

                      {(resource.age_min || resource.age_max) && (
                        <small>
                          {resource.age_min || "?"}-
                          {resource.age_max || "?"} años
                        </small>
                      )}
                    </div>

                    {resource.objective && (
                      <p className="resource-objective">
                        <strong>Objetivo:</strong> {resource.objective}
                      </p>
                    )}

                    <div className="resource-actions">
                      {resource.content_type === "interactive_game" ? (
                        <button
                          type="button"
                          className="play-button"
                          onClick={() => openResource(resource)}
                        >
                          <i className="fas fa-play"></i>
                          <span>Jugar</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="open-button"
                          onClick={() => openResource(resource)}
                        >
                          <i className="fas fa-folder-open"></i>
                          <span>Abrir</span>
                        </button>
                      )}
                    </div>
                  </article>
                ))
              )}
            </section>
          </>
        )}

        {activeTab === "subir" && (
          <form
            className="upload-resource-form"
            onSubmit={handleUploadResource}
          >
            <div className="form-grid">
              <input
                name="title"
                placeholder="Nombre del recurso"
                value={form.title}
                onChange={handleFormChange}
                required
              />

              <input
                name="age_min"
                type="number"
                placeholder="Edad mínima"
                value={form.age_min}
                onChange={handleFormChange}
              />

              <input
                name="age_max"
                type="number"
                placeholder="Edad máxima"
                value={form.age_max}
                onChange={handleFormChange}
              />

              <input
                name="objective"
                placeholder="Objetivo específico"
                value={form.objective}
                onChange={handleFormChange}
              />

              <select
                name="resource_type"
                value={form.resource_type}
                onChange={handleFormChange}
              >
                <option value="">Tipo de recurso</option>
                <option value="ficha">Ficha</option>
                <option value="juego">Juego</option>
                <option value="cuento">Cuento</option>
                <option value="pauta_padres">Pauta para padres</option>
                    <option value="evaluacion">Evaluación</option>
              </select>

              {form.resource_type === "juego" && (
                <select
                  name="difficulty"
                  value={form.difficulty}
                  onChange={handleFormChange}
                >
                  <option value="">Dificultad</option>
                  <option value="facil">Fácil</option>
                  <option value="medio">Medio</option>
                  <option value="dificil">Difícil</option>
                </select>
              )}
            </div>

            <div className="resource-multi-field">
              <span className="resource-multi-label">
                Especialidades
              </span>

              <p className="resource-field-help">
                Selecciona todas las especialidades en las que debe aparecer
                este recurso.
              </p>

              <div className="resource-chip-selector">
                {SPECIALTY_OPTIONS.map((option) => {
                  const selected = form.specialties.includes(option.value);

                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`resource-select-chip ${
                        selected ? "selected" : ""
                      }`}
                      onClick={() => toggleSpecialty(option.value)}
                      aria-pressed={selected}
                    >
                      {selected && (
                        <i
                          className="fas fa-check"
                          style={{ marginRight: 6 }}
                        ></i>
                      )}
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="form-grid">
              <select
                name="area_folder_id"
                value={form.area_folder_id}
                onChange={handleCategoryChange}
                required
                disabled={form.specialties.length === 0}
              >
                <option value="">
                  {form.specialties.length === 0
                    ? "Primero selecciona una especialidad"
                    : loadingCategories
                    ? "Cargando áreas..."
                    : "Área"}
                </option>

                {formCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.specialty_label
                      ? `${category.specialty_label} · ${category.name}`
                      : category.name}
                  </option>
                ))}
              </select>

              <select
                name="subarea_folder_id"
                value={form.subarea_folder_id}
                onChange={handleSubcategoryChange}
                disabled={!form.area_folder_id}
              >
                <option value="">
                  {loadingSubcategories
                    ? "Cargando subáreas..."
                    : "Subárea"}
                </option>

                {subcategories.map((subcategory) => (
                  <option key={subcategory.id} value={subcategory.id}>
                    {subcategory.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="new-subcategory-row">
              <input
                type="text"
                placeholder="Crear nueva subárea"
                value={newSubcategoryName}
                onChange={(e) => setNewSubcategoryName(e.target.value)}
                disabled={!form.area_folder_id}
              />

              <button
                type="button"
                onClick={handleCreateSubcategory}
                disabled={!form.area_folder_id}
              >
                Crear
              </button>
            </div>

            <div className="resource-tags-form">
              <span className="resource-multi-label">Etiquetas</span>

              <div className="resource-new-tag-row">
                <select
                  value={tagToAdd}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTagToAdd(value);
                    addExistingTag(value);
                  }}
                  disabled={loadingTags}
                >
                  <option value="">
                    {loadingTags
                      ? "Cargando etiquetas..."
                      : "Seleccionar etiqueta..."}
                  </option>

                  {availableTagsToAdd.map((tag) => (
                    <option key={tag.id || tag.name} value={tag.name}>
                      {tag.name}
                    </option>
                  ))}
                </select>
              </div>

              {form.tags.length > 0 && (
                <div className="resource-chip-selector">
                  {form.tags.map((tagName) => (
                    <button
                      key={tagName}
                      type="button"
                      className="resource-select-chip selected"
                      onClick={() => removeTag(tagName)}
                      title="Quitar etiqueta"
                    >
                      {tagName}
                      <span style={{ marginLeft: 7 }}>×</span>
                    </button>
                  ))}
                </div>
              )}

              <div className="resource-new-tag-row">
                <input
                  type="text"
                  placeholder="Crear nueva etiqueta"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCreateTag();
                    }
                  }}
                />

                <button
                  type="button"
                  onClick={handleCreateTag}
                  disabled={!newTagName.trim()}
                >
                  Crear
                </button>
              </div>
            </div>

            <textarea
              name="description"
              placeholder="Descripción breve"
              value={form.description}
              onChange={handleFormChange}
            />

            <textarea
              name="notes"
              placeholder="Observaciones de uso"
              value={form.notes}
              onChange={handleFormChange}
            />

            <div className="resource-file-upload">
              <label className="resource-file-picker">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) =>
                    setFiles(Array.from(e.target.files || []))
                  }
                  style={{ display: "none" }}
                />

                <i className="fas fa-cloud-upload-alt"></i>
                <span>
                  {files.length === 0
                    ? "Seleccionar archivos"
                    : "Añadir o cambiar archivos"}
                </span>
                <small>
                  PDF, JPG, PNG, DOC o DOCX · puedes seleccionar varios
                </small>
              </label>

              {files.length > 0 && (
                <div className="selected-files-list">
                  <strong>
                    {files.length === 1
                      ? "1 archivo seleccionado"
                      : `${files.length} archivos seleccionados`}
                  </strong>

                  <ul>
                    {files.map((selectedFile, index) => (
                      <li key={`${selectedFile.name}-${index}`}>
                        <span>{selectedFile.name}</span>

                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          aria-label={`Quitar ${selectedFile.name}`}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <button type="submit">
              {files.length > 1
                ? `Subir ${files.length} recursos`
                : "Subir recurso"}
            </button>
          </form>
        )}
      </div>
    </>
  );
}
